/*
 * Subscribe & Save / One-time purchase box (snippets/product-purchase-options.liquid).
 * Owns the product form's `selling_plan` input and keeps option prices in sync with the selected variant.
 *
 * Public hooks for other features (sticky ATC, buy button, etc.):
 * - Event `purchase-options:change` (bubbles to document) with `detail`:
 *   { sectionId, productId, variantId, purchaseType: 'subscription' | 'onetime', sellingPlanId, price, compareAtPrice,
 *     oneTimePrice, oneTimeCompareAtPrice, subscriptionPrice, subscriptionCompareAtPrice, discountPercent, discountAmount }
 *   All prices are in cents; `compareAtPrice` is 0 when there is nothing to strike through.
 * - The same state is mirrored as data attributes on <purchase-options> and on the closest <product-info>:
 *   data-purchase-type, data-selling-plan-id, data-purchase-price, data-purchase-compare-price
 * - `element.state` returns the latest detail object, `element.setPurchaseType('subscription' | 'onetime')` changes it.
 * - `element.setQuantity(n)` shows option prices for n units (used by the bar pack selector on products without
 *   variants). Detail prices stay per unit; `quantity`, `totalPrice` and `totalCompareAtPrice` are added.
 */
(function () {
  'use strict';

  if (customElements.get('purchase-options')) return;

  const formatMoney = (cents) => {
    if (window.theme && window.theme.formatMoney) return window.theme.formatMoney(cents, window.theme.moneyFormat);
    return `$${(cents / 100).toFixed(2)}`;
  };

  class PurchaseOptions extends HTMLElement {
    connectedCallback() {
      try {
        this.data = JSON.parse(this.querySelector('[data-purchase-options-data]').textContent);
      } catch (error) {
        return;
      }

      this.form = document.getElementById(this.dataset.formId);
      if (!this.form) return;

      this.productInfo = this.closest('product-info');
      this.radios = Array.from(this.querySelectorAll('[data-purchase-type-input]'));
      this.frequencyWrapper = this.querySelector('[data-purchase-options-frequency-wrapper]');
      this.frequency = this.querySelector('[data-purchase-options-frequency]');
      this.sellingPlanInput = this.getSellingPlanInput();

      const variantInput = this.form.querySelector('input[name="id"]');
      this.variantId = String((variantInput && variantInput.value) || this.dataset.variantId);
      this.preferredType = this.dataset.purchaseType || this.dataset.defaultSelection || 'subscription';
      this.sellingPlanId = this.dataset.sellingPlanId || null;
      this.quantity = this.quantity || 1;

      this.onRadioChange = (event) => {
        this.preferredType = event.target.value;
        this.update();
      };
      this.onFrequencyChange = () => {
        this.sellingPlanId = this.frequency.value;
        this.preferredType = 'subscription';
        this.update();
      };
      // product-info updates the form's `id` input and dispatches a bubbling change event on variant change
      this.onFormChange = (event) => {
        if (event.target && event.target.name === 'id') this.setVariant(event.target.value);
      };

      this.radios.forEach((radio) => radio.addEventListener('change', this.onRadioChange));
      this.frequency?.addEventListener('change', this.onFrequencyChange);
      this.form.addEventListener('change', this.onFormChange);

      if (window.subscribe && window.theme && window.theme.PUB_SUB_EVENTS) {
        this.variantChangeUnsubscriber = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (variant) this.setVariant(variant.id);
        });
      }

      this.update();
    }

    disconnectedCallback() {
      this.radios?.forEach((radio) => radio.removeEventListener('change', this.onRadioChange));
      this.frequency?.removeEventListener('change', this.onFrequencyChange);
      this.form?.removeEventListener('change', this.onFormChange);
      this.variantChangeUnsubscriber?.();
    }

    get state() {
      return this.currentState || null;
    }

    setPurchaseType(type) {
      if (type !== 'subscription' && type !== 'onetime') return;
      this.preferredType = type;
      this.update();
    }

    setQuantity(quantity) {
      quantity = Math.max(1, parseInt(quantity, 10) || 1);
      if (quantity === this.quantity) return;
      this.quantity = quantity;
      if (this.data) this.update();
    }

    setVariant(variantId) {
      variantId = String(variantId || '');
      if (!variantId || variantId === this.variantId || !this.data.variants[variantId]) return;
      this.variantId = variantId;
      this.update();
    }

    // Reuse the form's selling_plan input (inside the <form> so every serializer, incl. Rebuy, picks it up)
    getSellingPlanInput() {
      let input = this.form.querySelector('input[name="selling_plan"]');

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        this.form.appendChild(input);
      }

      input.setAttribute('data-purchase-options-selling-plan', '');
      return input;
    }

    update() {
      const variant = this.data.variants[this.variantId];
      if (!variant) return;

      const plans = variant.plans || [];
      const plan = plans.find((item) => String(item.id) === String(this.sellingPlanId)) || plans[0] || null;
      const oneTimePrice = variant.price;
      const oneTimeCompareAtPrice = variant.compareAtPrice > variant.price ? variant.compareAtPrice : 0;

      let purchaseType = this.preferredType;
      if (this.data.requiresSellingPlan) purchaseType = 'subscription';
      if (!plan) purchaseType = 'onetime';

      const subscriptionPrice = plan ? plan.price : null;
      const subscriptionCompareAtPrice = plan && Math.max(oneTimeCompareAtPrice, oneTimePrice) > plan.price ? Math.max(oneTimeCompareAtPrice, oneTimePrice) : 0;
      const discountAmount = plan && oneTimePrice > plan.price ? oneTimePrice - plan.price : 0;
      const discountPercent = discountAmount ? Math.round((discountAmount * 100) / oneTimePrice) : 0;
      const isSubscription = purchaseType === 'subscription';

      this.sellingPlanId = plan ? String(plan.id) : null;

      // Form input: disabled for one-time purchases so no selling_plan is submitted
      this.sellingPlanInput.value = isSubscription ? this.sellingPlanId : '';
      this.sellingPlanInput.disabled = !isSubscription;

      this.renderOptions(plans, plan, purchaseType);
      this.renderPrices({oneTimePrice, oneTimeCompareAtPrice, subscriptionPrice, subscriptionCompareAtPrice, discountPercent, discountAmount}, this.quantity || 1);

      const detail = {
        sectionId: this.dataset.sectionId,
        productId: this.dataset.productId,
        variantId: this.variantId,
        purchaseType,
        sellingPlanId: isSubscription ? this.sellingPlanId : null,
        price: isSubscription ? subscriptionPrice : oneTimePrice,
        compareAtPrice: isSubscription ? subscriptionCompareAtPrice : oneTimeCompareAtPrice,
        oneTimePrice,
        oneTimeCompareAtPrice,
        subscriptionPrice,
        subscriptionCompareAtPrice,
        discountPercent,
        discountAmount,
        quantity: this.quantity || 1,
      };
      detail.totalPrice = detail.price === null ? null : detail.price * detail.quantity;
      detail.totalCompareAtPrice = detail.compareAtPrice * detail.quantity;

      this.currentState = detail;
      [this, this.productInfo].forEach((element) => {
        if (!element) return;
        element.dataset.purchaseType = purchaseType;
        element.dataset.sellingPlanId = detail.sellingPlanId || '';
        element.dataset.purchasePrice = detail.price;
        element.dataset.purchaseComparePrice = detail.compareAtPrice;
      });

      this.dispatchEvent(new CustomEvent('purchase-options:change', {bubbles: true, detail}));
    }

    renderOptions(plans, plan, purchaseType) {
      this.radios.forEach((radio) => {
        const option = radio.closest('[data-purchase-option]');
        const isSubscriptionRadio = radio.value === 'subscription';

        if (isSubscriptionRadio) {
          radio.disabled = !plan;
          option.hidden = !plan;
        }

        radio.checked = radio.value === purchaseType;
        option.classList.toggle('is-selected', radio.checked);
      });

      if (!this.frequency) return;

      const planIds = plans.map((item) => String(item.id)).join(',');
      if (this.frequency.dataset.planIds !== planIds) {
        this.frequency.dataset.planIds = planIds;
        this.frequency.innerHTML = '';
        plans.forEach((item) => this.frequency.add(new Option(item.name, item.id)));
      }

      if (plan) this.frequency.value = String(plan.id);
      this.frequencyWrapper.hidden = plans.length < 2;
    }

    renderPrices(prices, quantity = 1) {
      const setPrice = (selector, cents) => {
        this.querySelectorAll(selector).forEach((element) => {
          if (cents) element.textContent = formatMoney(cents * quantity);
          if (element.tagName === 'S') element.hidden = !cents;
        });
      };

      setPrice('[data-purchase-options-price="onetime"]', prices.oneTimePrice);
      setPrice('[data-purchase-options-compare="onetime"]', prices.oneTimeCompareAtPrice);
      setPrice('[data-purchase-options-price="subscription"]', prices.subscriptionPrice);
      setPrice('[data-purchase-options-compare="subscription"]', prices.subscriptionCompareAtPrice);

      this.querySelectorAll('[data-purchase-options-percent]').forEach((element) => (element.textContent = `${prices.discountPercent}%`));
      this.querySelectorAll('[data-purchase-options-amount]').forEach((element) => (element.textContent = formatMoney(prices.discountAmount * quantity)));
      this.querySelectorAll('[data-purchase-options-badge]').forEach((element) => (element.hidden = !prices.discountPercent));

      // Optional small "From" price under the product title
      const fromPrice = prices.subscriptionPrice ? Math.min(prices.subscriptionPrice, prices.oneTimePrice) : prices.oneTimePrice;
      (this.productInfo || document).querySelectorAll('[data-purchase-options-from-price]').forEach((element) => (element.textContent = formatMoney(fromPrice)));
    }
  }

  customElements.define('purchase-options', PurchaseOptions);
})();
